import { useEffect, useMemo, useState } from 'react';
import {
  applyAction,
  redactEvents,
  startMatch,
  view,
  type MatchEvent,
  type MatchState,
  type Replay,
  type Side,
} from '../../engine';
import { t } from '../../i18n';
import { namesFor } from '../../app/describe';
import type { Controller } from '../../app/matchHost';
import type { Settings } from '../../app/settings';
import { Board } from '../components/Board';
import { CardView } from '../components/CardView';
import { LogPanel } from '../components/LogPanel';
import { PileDialog } from '../components/MatchDialogs';
import { PileView } from '../components/PilePanel';
import { PlayerStrip } from '../components/PlayerStrip';
import { cardLong } from '../../app/describe';

interface Timeline {
  readonly states: MatchState[];
  /** events[i] are the events that led to states[i] (events[0]: setup). */
  readonly events: MatchEvent[][];
  readonly error: string | null;
}

/**
 * The match step by step, as far as the replay goes. An action the engine refuses ends it there,
 * with the reason. A replay whose match cannot even be set up (its rules are not valid, say: a
 * shared or imported file can hold anything) has no step at all, only the reason.
 */
function buildTimeline(replay: Replay): Timeline {
  const message = (e: unknown) => (e instanceof Error ? e.message : String(e));
  let start: ReturnType<typeof startMatch>;
  try {
    start = startMatch(replay.config, replay.seed);
  } catch (e) {
    return { states: [], events: [], error: message(e) };
  }
  const states = [start.state];
  const events = [start.events];
  for (const action of replay.actions) {
    try {
      const res = applyAction(states[states.length - 1] as MatchState, action);
      if (res.error) return { states, events, error: `${res.error.code}: ${res.error.message}` };
      states.push(res.state);
      events.push(res.events);
    } catch (e) {
      return { states, events, error: message(e) };
    }
  }
  return { states, events, error: null };
}

type Perspective = 'all' | Side;

interface ReplayViewerProps {
  readonly replay: Replay;
  readonly names: Readonly<Record<Side, string>>;
  readonly settings: Settings;
  readonly reduceMotion: boolean;
  readonly onBack: () => void;
}

export function ReplayViewer(props: ReplayViewerProps) {
  const timeline = useMemo(() => buildTimeline(props.replay), [props.replay]);
  if (timeline.states.length === 0)
    return (
      <main className="page page-replay">
        <header className="page-head">
          <button type="button" className="btn btn-small" onClick={props.onBack}>
            ← {t('menu.back')}
          </button>
          <h1>{t('replays.title')}</h1>
        </header>
        <p className="notice notice-error" role="alert">
          {t('replays.unplayable', { reason: timeline.error ?? '' })}
        </p>
      </main>
    );
  return <Steps {...props} timeline={timeline} />;
}

function Steps({
  names: playerNames,
  settings,
  reduceMotion,
  onBack,
  timeline,
}: ReplayViewerProps & { readonly timeline: Timeline }) {
  const last = timeline.states.length - 1;
  const [step, setStep] = useState(0);
  const [perspective, setPerspective] = useState<Perspective>('all');
  const [pileOpen, setPileOpen] = useState(false);
  const state = timeline.states[step] as MatchState;
  const viewSide: Side = perspective === 'all' ? 'S' : perspective;
  const v = view(state, viewSide);
  const controllers: Record<Side, Controller> = {
    S: { kind: 'human', name: playerNames.S || t('side.S') },
    N: { kind: 'human', name: playerNames.N || t('side.N') },
  };
  const names = namesFor(v, controllers);
  const log = useMemo(() => {
    const all = timeline.events.slice(0, step + 1).flat();
    return perspective === 'all' ? all : redactEvents(all, perspective);
  }, [timeline, step, perspective]);
  const lastEvents = timeline.events[step] ?? [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && e.shiftKey) setStep(last);
      else if (e.key === 'ArrowLeft' && e.shiftKey) setStep(0);
      else if (e.key === '.' || e.key === 'PageDown') setStep((s) => Math.min(last, s + 1));
      else if (e.key === ',' || e.key === 'PageUp') setStep((s) => Math.max(0, s - 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [last]);

  const hands = (perspective === 'all' ? (['N', 'S'] as const) : [perspective]).map((side) => (
    <div key={side} className="replay-hand">
      <span className="mini-label">
        {names.side(side)} · {t('match.hand')}
      </span>
      <ul className="hand-row">
        {state.players[side].hand.map((id) => (
          <li key={id}>
            <CardView
              size="sm"
              face={state.cards[id]?.face ?? null}
              wild={state.cards[id]?.kind === 'wild'}
              label={cardLong(state.cards[id])}
            />
          </li>
        ))}
      </ul>
    </div>
  ));
  const bottom: Side = perspective === 'N' ? 'N' : 'S';
  const strip = (side: Side, className: string) => (
    <PlayerStrip
      className={className}
      v={v}
      side={side}
      controller={controllers[side]}
      isViewer={false}
      showCounts
      thinking={false}
    />
  );

  return (
    <div className={`match replay ${settings.highContrast ? 'high-contrast' : ''}`}>
      <header className="match-bar">
        <h1 className="sr-only">{t('replays.title')}</h1>
        <button type="button" className="btn btn-small btn-quiet" onClick={onBack}>
          ← {t('menu.back')}
        </button>
        <span
          className="bar-round"
          role="img"
          aria-label={t('match.round', { round: v.round, max: v.config.constants.MAX_ROUNDS })}
        >
          <span className="bar-round-label" aria-hidden="true">
            {t('bar.round')}
          </span>
          <span className="round-dots" aria-hidden="true">
            {Array.from({ length: v.config.constants.MAX_ROUNDS }, (_, i) => {
              const r = v.rounds[i];
              return (
                <span
                  key={i}
                  className={`round-dot ${r ? `is-done ${r.winner ? `won-${r.winner}` : 'is-tie'}` : ''} ${
                    !r && i + 1 === v.round ? 'is-current' : ''
                  }`}
                />
              );
            })}
          </span>
        </span>
        <label className="inline-field bar-perspective">
          <span className="sr-only">{t('replays.perspective')}</span>
          <select
            value={perspective}
            aria-label={t('replays.perspective')}
            onChange={(e) => setPerspective(e.target.value as Perspective)}
          >
            <option value="all">{t('replays.omniscient')}</option>
            <option value="S">{names.side('S')}</option>
            <option value="N">{names.side('N')}</option>
          </select>
        </label>
      </header>
      <div className="match-body">
        {strip(bottom === 'S' ? 'N' : 'S', 'area-opp')}
        <main className="area-board">
          <Board
            view={v}
            viewer={bottom}
            names={names}
            lastEvents={lastEvents}
            eventsKey={step}
            reduceMotion={reduceMotion}
            label={t('replays.title')}
          />
        </main>
        {strip(bottom, 'area-me')}
        <section className="area-table" aria-label={t('replays.title')}>
          <div className="table-cards">
            <PileView v={v} onShowPile={() => setPileOpen(true)} />
          </div>
          <div className="table-talk">
            <div className="replay-controls" role="group" aria-label={t('replays.title')}>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setStep(0)}
                disabled={step === 0}
              >
                {t('replays.first')}
              </button>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
              >
                {t('replays.prev')}
              </button>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setStep(Math.min(last, step + 1))}
                disabled={step === last}
              >
                {t('replays.nextStep')}
              </button>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setStep(last)}
                disabled={step === last}
              >
                {t('replays.last')}
              </button>
            </div>
            <input
              className="replay-range"
              type="range"
              min={0}
              max={last}
              value={step}
              aria-label={t('replays.step', { i: step, n: last })}
              onChange={(e) => setStep(Number(e.target.value))}
            />
            <span className="dim small">{t('replays.step', { i: step, n: last })}</span>
            {timeline.error && (
              <p className="notice notice-error" role="alert">
                {timeline.error}
              </p>
            )}
          </div>
        </section>
        <section className="area-hand replay-hands" aria-label={t('match.hand')}>
          {hands}
        </section>
        <aside className="area-side replay-side">
          <LogPanel events={log} names={names} />
        </aside>
      </div>
      {pileOpen && <PileDialog v={v} names={names} onClose={() => setPileOpen(false)} />}
    </div>
  );
}
